/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-03 16:11:50
 **/

import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { useAtomValue } from "jotai";
import { useCallback, useState } from "react";

import { Column, Icon, Typography, useToast } from "@/frameworks/designStuff/components";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { _T } from "@rfice/language";

import { APIErrorCode, ModalID } from "@/frameworks/core/commons";
import { PhoneCallAtoms, usePhoneCall, useSimplePopup } from "@/frameworks/core/hooks";

type DialButtonProps = {
	callID: string;
	isCallEnded: boolean;
	disabled?: boolean;
	isBlockedByMe?: boolean;
} & Partial<WebServices.People.List.User>;

const LOGTAG = "#DialButton#";

export default function DialButton(props: DialButtonProps) {
	const { callID, isCallEnded } = props;

	const { colors } = useTheme();
	const toast = useToast();
	const phoneCall = usePhoneCall();
	const callees = phoneCall.state.callees[0];
	const phoneDetail = phoneCall.state.phone_detail;
	const setCallID = phoneCall.state.id[1];
	const callStatus = phoneCall.state.status(callID)[0];
	const [isStartDial, setStartDial] = useState(false);
	const callList = useAtomValue(PhoneCallAtoms.CallList, { store: JotaiStore });
	const isMyCall = Object.keys(callList).includes(props.callID);
	const simplePopup = useSimplePopup();
	const existCallID = Object.keys(callList)?.[0] ?? "";
	const participatedUser = phoneCall.state.participatedUserList(existCallID);

	const handleDialing = useCallback(async () => {
		try {
			if (callees[0]?.blockedByMe && props?.isBlockedByMe) {
				toast.show({ type: "system", message: _T("차단한 사용자입니다. 차단 해제 후 전화를 걸어주세요.") });
				return;
			}
			if (isStartDial) {
				setStartDial(false);
				return;
			}

			//통화 시작 전일 때
			if (callID.length === 0) {
				setStartDial(true);
				const newCallID = await phoneCall
					.start_dial({
						type: "normal",
						callerIDs: callees.reduce<string[]>((total, current) => {
							total.push(current.userID);
							return total;
						}, []),
						busyUsers: (userIDs: string[]) => {
							// 이미 통화 중인 사람들 목록
							console.debug(LOGTAG, "Busy users", userIDs);
						}
					})
					.catch((error) => {
						switch (error.code) {
							case APIErrorCode.RECEIVER_ACTIVE_CALL_EXISTS: {
								simplePopup(ModalID.CALL_EXIST_POPUP, _T("상대방이 이미 통화 중입니다."));
								return "";
							}

							case APIErrorCode.CALL_USER_MAX_COUNT_EXCEEDED: {
								simplePopup(ModalID.CALL_EXIST_POPUP, _T("그렇게나 많이?"));
								return "";
							}

							case APIErrorCode.DATA_NOT_FOUND: {
								simplePopup(ModalID.CALL_EXIST_POPUP, _T("존재하지 않는 상대방이 포함되어 있습니다."));
								return "";
							}

							case APIErrorCode.ALL_RECEIVER_BUSY: {
								simplePopup(ModalID.CALL_EXIST_POPUP, _T("상대방이 이미 통화 중입니다."));
								return "";
							}

							case APIErrorCode.EXCEED_MAX_CALL_PER_ACCOUNT: {
								simplePopup(ModalID.CALL_EXIST_POPUP, _T("다른 디바이스에서 이미 통화 중입니다."));
								return "";
							}
						}

						throw error;
					});
				setStartDial(false);
				newCallID.length > 0 && setCallID(newCallID);
			} else {
				await phoneCall.call_end(callID);
			}
		} catch (error: unknown) {
			console.warn(LOGTAG, `dial fail:`, error);
			setStartDial(false);
		}
	}, [callID, callees, callStatus]);

	return (
		<Column horizonAlign="center" verticalAlign="center">
			{existCallID &&
			["floating", "fullSizeShare"].includes(phoneCall.popup.info(existCallID)?.uiType) &&
			participatedUser.filter((element) => phoneDetail[0]?.callees?.map((user) => user.userID).includes(element))
				.length > 0 ? (
				<Typography typoSize="Title1_B">{_T("현재 상대방과 전화 연결중입니다.")}</Typography>
			) : (
				<CallButton
					onClick={handleDialing}
					dialStatus={callStatus}
					disabled={
						isStartDial ||
						isCallEnded ||
						callStatus === "END" ||
						(Boolean(phoneCall.status(existCallID)) && !isMyCall)
					}
				>
					<Icon name="Call0224" size={24} fill={colors.neutral_00_100} />
				</CallButton>
			)}
		</Column>
	);
}

export const CallButton = styled.button<{
	dialStatus: PushServices.Call.CallStatus;
	disabled: boolean;
}>`
	width: 136px;
	height: 40px;
	display: flex;
	border-radius: 20px;
	border: none;
	background-color: ${({ dialStatus, disabled, theme }) => {
		if (disabled && !dialStatus) {
			return theme.colors.error_60_100;
		}
		switch (dialStatus) {
			case "READY":
			case "ONGOING":
				return theme.colors.error_60_100;
			default:
				return theme.colors.success_60_100;
		}
	}};
	cursor: pointer;
	justify-content: center;
	align-items: center;

	&:disabled {
		background-color: ${({ theme }) => theme.colors.neutral_30_100};
		cursor: not-allowed;
	}
`;
